import { isNumber } from "common/utils/numbers";
import type { ReactNode } from "react";
import { useCallback, useContext, useEffect, useId, useRef, useState } from "react";
import ReactDOM from "react-dom";
import cx from "ui/cx";
import type { AppContextProps } from "ui/hooks/useAppContext";
import { AppContext } from "ui/hooks/useAppContext";
import useBrowserLayoutEffect from "ui/hooks/useBrowserLayoutEffect";
import useEscapeKey from "ui/hooks/useEscapeKey";
import useOutsideClick from "ui/hooks/useOutsideClick";
import useWindowSize from "ui/hooks/useWindowSize";
import IconAdd from "ui/icons/IconAdd";
import styles from "./ContextMenu.module.scss";
import { useTheme } from "./useTheme";

interface ContextMenuProps {
  children: ReactNode;
  closeOnClick?: boolean;
  open: boolean;
  setOpen: (e: PointerEvent, open: boolean) => void;
  mouseEvent?: "contextmenu" | "mousedown";
  closeIcon?: boolean;
  className?: string;
  x?: number;
  y?: number;
  /** When set, position the menu so its right edge is at this x (opens to the left of the point). */
  alignRightToX?: number;
  title?: ReactNode;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  /** Listen for outside clicks in the capture phase (ignores stopPropagation on targets). */
  captureOutsideClick?: boolean;
}

export default function ContextMenu({
  children,
  open,
  setOpen,
  mouseEvent = "contextmenu",
  closeIcon = false,
  closeOnClick = true,
  className,
  x,
  y,
  alignRightToX,
  title,
  onMouseEnter,
  onMouseLeave,
  captureOutsideClick = false,
}: ContextMenuProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const { isDarkMode } = useTheme();
  const { modalContainerRef } = useContext<AppContextProps>(AppContext);
  const [windowHeight, windowWidth] = useWindowSize();
  const contextMenuId = useId();
  const [coords, setCoords] = useState({ x: 0, y: 0 });
  const mouseHandlerRef = useRef<((e: MouseEvent) => void) | undefined>(undefined);

  const handleOutsideClick = useCallback(() => {
    const event = { stopPropagation: () => {} } as PointerEvent;
    setOpen(event, false);
  }, [setOpen]);

  useOutsideClick(containerRef, handleOutsideClick, false, captureOutsideClick);
  useEscapeKey(() => setOpen({ stopPropagation: () => {} } as PointerEvent, false));

  useBrowserLayoutEffect(() => {
    if (x === undefined && y === undefined) {
      const handler = (e: MouseEvent) => {
        if (mouseEvent === "mousedown" && e.button !== 0) {
          return;
        }
        if (!open) {
          setCoords({ x: e.clientX, y: e.clientY });
        }
      };
      mouseHandlerRef.current = handler;
      document.addEventListener("mousedown", handler);

      return () => {
        document.removeEventListener("mousedown", handler);
        mouseHandlerRef.current = undefined;
      };
    } else if (isNumber(x) && isNumber(y)) {
      setCoords({ x, y });
    }

    return undefined;
  }, [open, x, y, mouseEvent]);

  useEffect(() => {
    if (!open || !containerRef.current) {
      return;
    }

    const container = containerRef.current;
    const menuHeight = container.offsetHeight;
    const menuWidth = container.offsetWidth;
    const spaceBelow = windowHeight - coords.y;
    const spaceRight = windowWidth - coords.x;

    // Position above cursor if not enough space below
    if (spaceBelow < menuHeight) {
      container.style.top = `${coords.y - menuHeight}px`;
    } else {
      container.style.top = `${coords.y}px`;
    }

    // Horizontal: align right edge to x when requested, else fit in viewport
    if (alignRightToX !== undefined) {
      container.style.left = `${alignRightToX - menuWidth}px`;
    } else if (spaceRight < menuWidth) {
      container.style.left = `${coords.x - menuWidth}px`;
    } else {
      container.style.left = `${coords.x}px`;
    }
  }, [open, coords.x, coords.y, windowWidth, windowHeight, alignRightToX]);

  if (!modalContainerRef?.current || !open) {
    return null;
  }

  return ReactDOM.createPortal(
    <div
      ref={containerRef}
      id={contextMenuId}
      style={{
        position: "fixed",
        visibility: open ? "visible" : "hidden",
      }}
      onClick={(e) => {
        e.stopPropagation();
        if (closeOnClick) {
          setOpen(e as any, false);
        }
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      className={cx(styles.wrapper, className, isDarkMode && styles.dark)}
    >
      {title && <div className={cx(styles.menuTitle, isDarkMode && styles.dark)}>{title}</div>}
      <div className={styles.subWrapper}>
        {closeIcon && (
          <span className={styles.closeIconWrapper} onClick={(e) => setOpen(e as any, false)}>
            <IconAdd rotate={45} />
          </span>
        )}
        {children}
      </div>
    </div>,
    modalContainerRef.current,
  );
}
